import React from 'react';
import { Header } from '../components/Header';
import { LeftPanel } from '../components/LeftPanel';
import { useTheme } from '../hooks/useTheme';
import {  getAuthData, useAuth } from '../contexts/AuthContext';
import { useChat } from '../hooks';
import { notify } from '../utils/toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatContainer } from '../components/chat/ChatContainer';
import { HeaderActions } from '../components/header/HeaderActions';
import { storage } from '../lib/storage';
import { ToolMode } from '../constants/toolModes';
import { Message } from '../types/chat';
import { fetchSharedConversation } from '../services/api/sharedConversationsApi';

export const ChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract path segments to handle different URL formats
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isSharedConversation = pathSegments.length >= 2 && pathSegments[0] === 'convo' && pathSegments[1] === 'shared' ? 'shared' : ''  ;
  const conversationId = isSharedConversation=== 'shared'  ? pathSegments[2] : pathSegments.length >= 2 ? pathSegments[1] : null;
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [profile, setProfile] = React.useState<any>(null);
  const { state: { user }, signOut } = useAuth();
  const {
    theme,
    toggleTheme,
    getThemeIcon,
    getThemeText
  } = useTheme();

  
  // For shared conversations
  const [sharedMessages, setSharedMessages] = React.useState<Message[]>([]);
  const [loadingShared, setLoadingShared] = React.useState(false);
  
  // Function to fetch shared conversation messages
  const loadSharedConversation = React.useCallback(async (conversationId: string) => {
    try {
      setLoadingShared(true);
      const auth = await getAuthData();
      const formattedMessages = await fetchSharedConversation(conversationId, auth);
     
      
      if (!formattedMessages) {
        throw new Error('Failed to load shared conversation');
      }
      setSharedMessages(formattedMessages);
      return true;
    } catch (error: any) {
      console.error('Error loading shared conversation:', error);
      notify.error(error.message || 'Failed to load shared conversation');
      return false;
    } finally {
      setLoadingShared(false);
    }
  }, [getAuthData]);

  React.useEffect(() => {
    const loadProfile = async () => {
      if (user?.address) {
        const profileData = await storage.getProfile(user.address);
        setProfile(profileData);
      }
    };
    loadProfile();
  }, [user?.address]);

  // Store the credit update handler from Header
  const [creditUpdateHandler, setCreditUpdateHandler] = React.useState<((credit: number) => void) | null>(null);

  // Handler for credit updates from streamHandlers
  const handleCreditUpdate = React.useCallback((newCredit: number) => {
    // Update local state
    setProfile((prev: any) => prev ? { ...prev, credit: newCredit } : null);
    // Update Header through its handler
    if (creditUpdateHandler) {
      creditUpdateHandler(newCredit);
    }
  }, [creditUpdateHandler]);

  // Handler for Header's credit update registration
  const handleHeaderCreditUpdate = React.useCallback((handler: (credit: number) => void) => {
    setCreditUpdateHandler(() => handler);
  }, []);
  

  const {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loading,
    sending,
    createConversation,
    sendMessage,
    stopConversation,
    hasNewChat,
    regenerateMessage,
    deleteConversation,
    togglePin,
    syncConversations,
    tools,
    setTools
  } = useChat(handleCreditUpdate, loadSharedConversation, navigate);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleNewChat = React.useCallback(() => {
    if (!hasNewChat) {
      createConversation();
      navigate('/convo/#', { replace: true });
    }
  }, [hasNewChat, createConversation, navigate]);

  // Track if we've handled the initial URL
  const initialUrlHandled = React.useRef(false);
  
  // Sync conversations with backend when the page loads (only once)
  const syncedRef = React.useRef(false);
  
  React.useEffect(() => {
    const syncWithBackend = async () => {
      try {
        if (user?.address && !syncedRef.current) {
          syncedRef.current = true; // Set flag to prevent multiple syncs
          await syncConversations();
        }
      } catch (error) {
        console.error('Error syncing conversations:', error);
      }
    };
    
    syncWithBackend();
  }, [user?.address]); // Remove syncConversations from dependencies

  // Handle initial URL and conversation changes
  React.useEffect(() => {
    if (!initialUrlHandled.current) {
      if (isSharedConversation=== 'shared'  && conversationId) {
        // Handle shared conversation path
        initialUrlHandled.current = true;
        loadSharedConversation(conversationId);
       
      } else if (conversations?.length > 0) {
        // Handle regular conversation path
        initialUrlHandled.current = true;
        
        if (conversationId) {
          const conversation = conversations.find(c => c.id === conversationId);
          if (conversation) {
            setCurrentConversation(conversation);
          }
        } else if (location.hash === '#') {
          handleNewChat();
        }
      }
    }
  }, [conversations, isSharedConversation, conversationId, loadSharedConversation]);

  // Update URL only on explicit conversation changes
  React.useEffect(() => {
    if (initialUrlHandled.current && currentConversation) {
      // Clear shared messages when switching to a regular conversation
      if (isSharedConversation === 'shared' && sharedMessages.length > 0) {
        setSharedMessages([]);
      }
      
      const path = currentConversation.isNew ? '/convo/#' : `/convo/${currentConversation.id}`;
      navigate(path, { replace: true });
    }
  }, [currentConversation?.id]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <LeftPanel
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onConversationSelect={setCurrentConversation}
          onNewChat={handleNewChat}
          activeConversation={currentConversation}
          conversations={conversations}
          hasNewChat={hasNewChat}
          loading={loading}
          sending={sending}
          onDeleteConversation={deleteConversation}
          onSyncConversations={async () => {
            try {
              await syncConversations();
              notify.success('Conversations synchronized with server');
            } catch (error) {
              console.error('Error syncing conversations:', error);
              notify.error('Failed to sync conversations');
            }
          }}
        />

      <div className="flex-1 flex flex-col relative overflow-hidden">
        <Header
          toggleSidebar={toggleSidebar}
          theme={theme}
          toggleTheme={toggleTheme}
          getThemeIcon={getThemeIcon}
          getThemeText={getThemeText}
          onSignOut={signOut}
          sidebarOpen={sidebarOpen}
          onNewChat={handleNewChat}
          hasNewChat={hasNewChat}
          onCreditUpdate={handleHeaderCreditUpdate}
        />

        <ChatContainer
          messages={isSharedConversation ? sharedMessages : messages}
          loading={isSharedConversation=== 'shared'  ? loadingShared : loading}
          isSharedConversation={isSharedConversation}
          sending={sending}
          onSend={(content: string, tools?: ToolMode[]) => sendMessage(content, tools || [])}
          onStop={stopConversation}
          currentConversation={isSharedConversation=== 'shared' ? { 
            id: conversationId || '', 
            title: 'Shared Conversation',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            isPinned: false,
            status: 'active'
          } : currentConversation}
          onRegenerate={regenerateMessage}
          onTogglePin={togglePin}
          tools={tools}
          onToolsChange={setTools}
          onSyncConversations={async () => {
            await syncConversations();
          }}
        />
      </div>
    </div>
  );
};
